import { Link } from 'react-router-dom';
import {
  MapPin,
  Sparkles,
  Users,
  ShieldCheck,
  Compass,
  BookOpen,
  Bell,
  Star,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Footer from '../components/Footer';

const APPLE_STORE_URL =
  (import.meta.env.VITE_APPLE_STORE_URL as string | undefined) ||
  'https://apps.apple.com/ch/app/blip-the-travel-app/id6757182662';
const PLAY_STORE_URL =
  (import.meta.env.VITE_PLAY_STORE_URL as string | undefined) ||
  'https://play.google.com/store/apps/details?id=com.blip.travel.app';

/** iPhone screenshot wrapped in a lightweight device bezel. */
const PhoneFrame = ({
  src,
  alt,
  className = '',
  priority = false,
}: {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
}) => (
  <div className={`rounded-[2rem] bg-gray-900 p-1.5 shadow-2xl ring-1 ring-black/10 ${className}`}>
    <div className="overflow-hidden rounded-[1.6rem] bg-black">
      <img
        src={src}
        alt={alt}
        width={600}
        height={1300}
        loading={priority ? 'eager' : 'lazy'}
        className="block h-auto w-full"
      />
    </div>
  </div>
);

const modes: { icon: LucideIcon; title: string; description: string }[] = [
  {
    icon: Bell,
    title: 'Always Explore',
    description:
      'Keep your phone in your pocket. As you approach a monument, hidden gem or local story, Blip sends a gentle notification with the details — no maps or research needed.',
  },
  {
    icon: Sparkles,
    title: 'Discovery Mode',
    description:
      'Curious to explore? Swipe through personalized suggestions for nearby attractions and tap any card to dive into a full set of captivating stories.',
  },
  {
    icon: Users,
    title: 'Child & Family Mode',
    description:
      'Leo & Mia turn every landmark into a playful, age-appropriate adventure — with educational challenges and quizzes that kids actually enjoy.',
  },
];

const screenshots: { src: string; alt: string; caption: string }[] = [
  {
    src: '/images/screenshots/map.webp',
    alt: 'Blip travel app map view showing points of interest around the Eiffel Tower in Paris',
    caption: 'Points of interest surface as you explore',
  },
  {
    src: '/images/screenshots/discover.webp',
    alt: 'Blip Discovery Mode showing a swipeable content card about the Eiffel Tower',
    caption: 'Swipe through nearby discoveries',
  },
  {
    src: '/images/screenshots/story.webp',
    alt: 'Blip immersive full-screen story card telling the history of the Eiffel Tower',
    caption: 'Immersive, full-screen stories',
  },
  {
    src: '/images/screenshots/swipe.webp',
    alt: 'Blip standard and junior content toggle on a discovery card',
    caption: 'Switch between Standard and Junior',
  },
  {
    src: '/images/screenshots/kids.webp',
    alt: 'Blip Child and Family Mode with illustrated characters Leo and Mia at the Eiffel Tower',
    caption: 'Kid-friendly adventures with Leo & Mia',
  },
  {
    src: '/images/screenshots/tours.webp',
    alt: 'Blip Tours screen listing self-guided walking tours of Paris',
    caption: 'Follow curated self-guided tours',
  },
];

const features: { icon: LucideIcon; title: string; description: string }[] = [
  {
    icon: MapPin,
    title: 'Location-based discovery',
    description:
      "Points of interest surface automatically as you move through a city, so you never miss the story behind what you're walking past.",
  },
  {
    icon: BookOpen,
    title: 'Authentic, curated content',
    description:
      'Stories about famous landmarks and hidden local gems, gathered from locals, guides and cultural experts for a genuine travel experience.',
  },
  {
    icon: Users,
    title: 'Built by a community of creators',
    description:
      'Local guides and travel creators add points of interest and curate themed adventures that you can follow anywhere in the world.',
  },
  {
    icon: Sparkles,
    title: 'Personalized guidance',
    description:
      'Suggestions are tailored to your location and interests, making every walk feel like a private, spontaneous city tour.',
  },
  {
    icon: ShieldCheck,
    title: 'No ads, no distractions',
    description:
      'Blip is completely free to download and use, with an uninterrupted, ad-free exploration experience.',
  },
  {
    icon: Compass,
    title: 'Perfect for spontaneous travel',
    description:
      "Whether you're a first-time visitor or a curious local, Blip helps you uncover the stories hidden in the streets around you.",
  },
];

const StoreBadges = ({ size = 'h-14' }: { size?: string }) => (
  <div className="flex flex-col items-center gap-4 sm:flex-row">
    <a
      href={APPLE_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download Blip on the App Store"
      className="transition-transform hover:scale-105"
    >
      <img src="/images/badges/app-store-badge.svg" alt="Download on the App Store" className={size} />
    </a>
    {PLAY_STORE_URL && (
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Get Blip on Google Play"
        className="transition-transform hover:scale-105"
      >
        <img src="/images/badges/google-play-badge.svg" alt="Get it on Google Play" className={size} />
      </a>
    )}
  </div>
);

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col">
      {/* Marketing header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/app-icon.png" alt="Blip logo" className="h-8 w-8" />
            <span className="text-xl font-bold text-primary-600 dark:text-primary-400">Blip</span>
          </Link>
          <nav className="flex items-center gap-5 text-sm font-medium">
            <a href="#how-it-works" className="hidden sm:inline text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
              How it works
            </a>
            <a href="#screenshots" className="hidden sm:inline text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
              Screenshots
            </a>
            <Link
              to="/creator"
              className="hidden sm:inline text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Creator Portal
            </Link>
            <a
              href="#download"
              className="inline-flex items-center rounded-full bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 font-semibold transition-colors"
            >
              Get the app
            </a>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-b from-background via-background-surface to-white dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
          {/* Soft brand glow */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary-300/40 blur-3xl dark:bg-primary-700/20" />
          <div aria-hidden="true" className="pointer-events-none absolute top-40 right-0 h-80 w-80 rounded-full bg-accent-yellow/40 blur-3xl dark:bg-primary-800/20" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid md:grid-cols-2 gap-12 lg:gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 dark:bg-primary-900/40 px-4 py-1.5 text-sm font-semibold text-primary-700 dark:text-primary-300 mb-6">
                <Star className="h-4 w-4 fill-current" />
                Your smart travel companion
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight text-gray-900 dark:text-white mb-6">
                Never walk past a great story again.
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-xl">
                Blip reveals the stories behind famous landmarks and hidden local gems as you
                explore any city. A free, ad-free travel companion for iOS and Android that turns
                every walk into a curated adventure.
              </p>
              <div id="download">
                <StoreBadges />
              </div>
              <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
                Free to download &middot; No ads &middot; Available worldwide
              </p>
            </div>

            {/* Real product screenshots */}
            <div className="relative flex justify-center md:justify-end">
              <PhoneFrame
                src="/images/screenshots/map.webp"
                alt="Blip map view showing points of interest around the Eiffel Tower in Paris"
                className="w-56 sm:w-64 lg:w-72 rotate-[-3deg]"
                priority
              />
              <PhoneFrame
                src="/images/screenshots/story.webp"
                alt="Blip immersive story card describing the history of the Eiffel Tower"
                className="hidden sm:block absolute -bottom-6 -right-2 w-40 lg:w-48 rotate-[6deg]"
              />
            </div>
          </div>
        </section>

        {/* How it works — the three modes */}
        <section id="how-it-works" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Three ways to explore
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Blip brings the right information to you at exactly the right moment — whether you&rsquo;re
              wandering solo or discovering the world with kids.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {modes.map(({ icon: Icon, title, description }) => (
              <div key={title} className="text-center px-4">
                <div className="mx-auto w-14 h-14 rounded-2xl bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center mb-5">
                  <Icon className="w-7 h-7 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Screenshot showcase */}
        <section id="screenshots" className="bg-gradient-to-b from-background to-white dark:from-gray-800/50 dark:to-gray-900 border-y border-gray-100 dark:border-gray-800 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                See Blip in action
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Rich content cards, immersive stories and playful adventures — all built around
                where you are.
              </p>
            </div>
          </div>
          {/* Horizontal scroll gallery of real screenshots */}
          <div className="flex gap-6 overflow-x-auto px-4 sm:px-6 lg:px-8 pb-4 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {screenshots.map(({ src, alt, caption }) => (
              <figure key={src} className="shrink-0 w-48 sm:w-56 snap-center">
                <PhoneFrame src={src} alt={alt} />
                <figcaption className="mt-4 text-center text-sm text-gray-600 dark:text-gray-300">
                  {caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Uncover the stories hiding in plain sight
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Blip merges expert and community insights so travelers and locals alike can discover
              authentic, curated points of interest wherever they go.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-800/50 hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Creator CTA */}
        <section className="bg-gray-50 dark:bg-gray-800/50 border-y border-gray-100 dark:border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-xl">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                Are you a content creator or local guide?
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                Use the Blip Creator Portal to add points of interest, write rich content cards,
                and curate themed adventures that show up for travelers exploring the map in the
                mobile app.
              </p>
            </div>
            <Link
              to="/creator"
              className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap"
            >
              <Compass className="w-5 h-5" />
              Open the Creator Portal
            </Link>
          </div>
        </section>

        {/* Bottom download CTA */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Get Blip on your phone
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-xl mx-auto">
            Available free on the App Store and Google Play. Start exploring your city&mdash;or the
            next one you visit&mdash;today.
          </p>
          <div className="flex justify-center">
            <StoreBadges />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LandingPage;
